import React, { useState, useEffect, useContext, useCallback, useRef, useLayoutEffect } from 'react';
import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime } from '@/lib/dates';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { toast } from 'sonner';
import { StatusBadge, TypeBadge, SubmissionBadge } from './StatusBadge';
import { AuthImage } from './AuthImage';
import RequestChainCard from './RequestChainCard';
import { PRIORITY_LABELS, PRIORITY_OPTIONS } from './priority';
import { SR_STATUS, ACTION_LABELS, roleLabel, formatRequestor } from './theme';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ArrowLeft, CheckCircle2, XCircle, HelpCircle, PauseCircle,
  MapPin, Building2, User, Clock, MessageSquare, Wrench,
  ShieldCheck, FileText, Mail, Download, Hash, CalendarClock, Paperclip, Upload, Trash2, AlertTriangle,
  History, Save, X, ChevronDown,
} from 'lucide-react';

const DGREEN = '#14432A'; // dark forest green — distinguishes Service Requests from navy Work Orders

const CustomerLogoImg = ({ customerId, token }) => {
  const [failed, setFailed] = React.useState(false);
  if (!customerId || failed) return null;
  return <img src={`${API}/customers/${customerId}/logo?auth=${token}`} onError={() => setFailed(true)} alt="" className="h-6 w-auto max-w-[96px] object-contain" />;
};
const SRBannerField = ({ label, children }) => (
  <div><div className="text-[10px] uppercase tracking-wide text-[#9ae6b4]">{label}</div><div className="text-sm font-medium text-white">{children}</div></div>
);
const srShortTitle = (t) => ((t || '').replace(/^above[- ]?scope\s*:?\s*/i, '').trim() || t || '—');

const fmtDate = (d) => _fmtDate(d) || '—';
const fmtDateTime = (d) => _fmtDateTime(d) || '—';

const FULFILLMENT_OPTIONS = [
  'Unassigned', 'TFS Internal Personnel', 'Assigned Location Personnel',
  'TFS Subcontractor', 'Third-Party Service Provider',
];

const ACTIONS = {
  approve: { label: 'Approve', endpoint: 'approve', icon: CheckCircle2, tone: 'bg-green-600 hover:bg-green-700', needsNote: false, title: 'Approve Request' },
  decline: { label: 'Decline', endpoint: 'decline', icon: XCircle, tone: 'bg-red-600 hover:bg-red-700', needsNote: true, title: 'Decline Request' },
  info: { label: 'Request Info', endpoint: 'request-info', icon: HelpCircle, tone: 'bg-blue-600 hover:bg-blue-700', needsNote: true, title: 'Request More Information' },
  hold: { label: 'Place on Hold', endpoint: 'hold', icon: PauseCircle, tone: 'bg-slate-600 hover:bg-slate-700', needsNote: false, title: 'Place Request on Hold' },
};

const ServiceRequestDetail = ({ requestId, canReview, canConvert, onBack, onChanged, onConverted }) => {
  const { token, user } = useContext(AuthContext);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const [sr, setSr] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // action key
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  // conversion state
  const [convOpen, setConvOpen] = useState(false);
  const [fieldUsers, setFieldUsers] = useState([]);
  const [conv, setConv] = useState({ priority_level: '', required_completion_date: '', fulfillment_type: 'Unassigned', assigned_to: '' });

  // acknowledgment preview state
  const [ackOpen, setAckOpen] = useState(false);
  const [ackData, setAckData] = useState(null);
  const [ackBusy, setAckBusy] = useState(false);
  const isOps = ['owner', 'admin', 'manager', 'tfs_operations'].includes(user?.role);
  const canEdit = ['owner', 'admin'].includes(user?.role);
  const canPurge = user?.role === 'owner';

  // ---- Inline edit + History + Purge ----
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeInfo, setPurgeInfo] = useState(null);
  const [mDetails, setMDetails] = useState(false);
  const dset = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const scrollAnchor = useRef(null);
  const captureAnchor = () => {
    try {
      const refY = 210;
      const secs = Array.from(document.querySelectorAll('[data-testid^="sr-section-"]'));
      let chosen = secs.find((el) => el.getBoundingClientRect().bottom > refY) || secs[secs.length - 1];
      scrollAnchor.current = chosen ? { el: chosen, top: chosen.getBoundingClientRect().top } : null;
    } catch { scrollAnchor.current = null; }
  };
  useLayoutEffect(() => {
    const a = scrollAnchor.current;
    if (!a || !a.el || !a.el.isConnected) return;
    const delta = a.el.getBoundingClientRect().top - a.top;
    if (Math.abs(delta) > 1) window.scrollBy(0, delta);
    scrollAnchor.current = null;
  }, [editing]);

  const enterEdit = () => {
    captureAnchor();
    setDraft({
      title: sr.title || '', description: sr.description || '',
      requested_by_name: sr.requested_by_name || '',
      requester_contact_name: sr.requester_contact_name || '',
      requester_contact_email: sr.requester_contact_email || '',
      requester_contact_phone: sr.requester_contact_phone || '',
      client_authorized_by: sr.client_authorized_by || '',
      client_authorized_by_email: sr.client_authorized_by_email || '',
      client_reference_number: sr.client_reference_number || '',
      client_po_number: sr.client_po_number || '',
      client_work_order_number: sr.client_work_order_number || '',
      priority_level: sr.priority_level || '',
      service_classification: sr.service_scope || sr.service_classification || '',
      status: sr.status || '',
      client_required_completion_date: (sr.client_required_completion_date || '').slice(0, 10),
      original_request_date: (sr.original_request_date || '').slice(0, 10),
      request_type: sr.request_type || '',
    });
    setEditing(true);
  };
  const cancelEdit = () => { captureAnchor(); setEditing(false); setDraft({}); };
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await axios.put(`${API}/service-requests/${requestId}`, {
        title: draft.title, description: draft.description,
        requested_by_name: draft.requested_by_name,
        requester_contact_name: draft.requester_contact_name,
        requester_contact_email: draft.requester_contact_email,
        requester_contact_phone: draft.requester_contact_phone,
        client_authorized_by: draft.client_authorized_by,
        client_authorized_by_email: draft.client_authorized_by_email,
        client_reference_number: draft.client_reference_number,
        client_po_number: draft.client_po_number,
        client_work_order_number: draft.client_work_order_number,
        priority_level: draft.priority_level || null,
        service_classification: draft.service_classification || null,
        status: draft.status || null,
        client_required_completion_date: draft.client_required_completion_date || null,
        original_request_date: draft.original_request_date || null,
        request_type: draft.request_type || null,
      }, authHeaders);
      toast.success('Service request saved');
      captureAnchor(); setEditing(false); setDraft({});
      await load(); onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save changes');
    } finally { setSavingEdit(false); }
  };

  const openPurge = async () => {
    setPurgeInfo(null); setPurgeOpen(true);
    try {
      const { data } = await axios.get(`${API}/service-requests/${requestId}/purge-preview`, authHeaders);
      setPurgeInfo(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load purge details'); setPurgeOpen(false); }
  };
  const runPurge = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/service-requests/${requestId}`, authHeaders);
      toast.success('Service request purged');
      setPurgeOpen(false); onChanged?.(); onBack?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Purge failed'); }
    finally { setBusy(false); }
  };

  const openAck = async () => {
    setAckBusy(true);
    try {
      const r = await axios.get(`${API}/service-requests/${requestId}/acknowledgment-preview`, authHeaders);
      setAckData(r.data);
      setAckOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to build acknowledgment preview');
    } finally {
      setAckBusy(false);
    }
  };

  const downloadAckPdf = async () => {
    try {
      const r = await axios.get(`${API}/service-requests/${requestId}/acknowledgment-pdf`, { ...authHeaders, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Failed to generate acknowledgment PDF');
    }
  };

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/service-requests/${requestId}`, authHeaders),
        axios.get(`${API}/service-requests/${requestId}/activity`, authHeaders),
      ]);
      setSr(a.data);
      setActivity(b.data || []);
    } catch (e) {
      toast.error('Failed to load request');
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  // ---- Attachments (durable object storage) ----
  const [attachments, setAttachments] = useState([]);
  const [attUploading, setAttUploading] = useState('');
  const [docCat, setDocCat] = useState('Receipt');
  const canAttach = ['owner', 'admin', 'manager', 'tfs_operations', 'field_user'].includes(user?.role);
  const canRemove = ['owner', 'admin', 'manager', 'tfs_operations'].includes(user?.role);
  const loadAttachments = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/service-requests/${requestId}/attachments`, authHeaders);
      setAttachments(r.data || []);
    } catch (e) { /* ignore */ }
  }, [requestId]);
  useEffect(() => { loadAttachments(); }, [loadAttachments]);
  const uploadAttachment = async (file, category) => {
    if (!file) return;
    setAttUploading(category);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('category', category);
      if (category === 'document') fd.append('doc_category', docCat);
      await axios.post(`${API}/service-requests/${requestId}/attachments`, fd, { headers: { ...authHeaders.headers, 'Content-Type': 'multipart/form-data' } });
      toast.success('Attachment added'); await loadAttachments();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setAttUploading(''); }
  };
  const removeAttachment = async (id) => {
    const reason = window.prompt('Reason for removing this attachment? (recorded in audit history)');
    if (reason === null) return;
    try {
      await axios.delete(`${API}/service-requests/${requestId}/attachments/${id}?reason=${encodeURIComponent(reason)}`, authHeaders);
      toast.success('Attachment removed'); await loadAttachments(); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Remove failed'); }
  };
  const attByCat = (c) => attachments.filter((a) => a.category === c);

  const runAction = async () => {
    const cfg = ACTIONS[dialog];
    if (cfg.needsNote && !note.trim()) { toast.error('A note is required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/service-requests/${requestId}/${cfg.endpoint}`, { notes: note || null }, authHeaders);
      toast.success(`${cfg.label} — done`);
      setDialog(null); setNote('');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const openConvert = async () => {
    try {
      const r = await axios.get(`${API}/work-orders/field-users`, authHeaders);
      setFieldUsers(r.data || []);
    } catch (e) { /* noop */ }
    setConv({ priority_level: '', required_completion_date: '', fulfillment_type: 'Unassigned', assigned_to: '' });
    setConvOpen(true);
  };

  const runConvert = async () => {
    if (!conv.priority_level) { toast.error('Select a TFS priority (P1–P5)'); return; }
    setBusy(true);
    try {
      const payload = {
        priority_level: conv.priority_level,
        required_completion_date: conv.required_completion_date || null,
        fulfillment_type: conv.fulfillment_type,
        assigned_to: conv.assigned_to || null,
      };
      const r = await axios.post(`${API}/service-requests/${requestId}/convert-to-work-order`, payload, authHeaders);
      toast.success(`Work Order ${r.data.tfs_work_order_number} created`);
      setConvOpen(false);
      onConverted?.(r.data.work_order_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Conversion failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="py-16 text-center text-slate-400">Loading…</div>;
  if (!sr) return <div className="py-16 text-center text-slate-400">Request not found.</div>;

  const isOpenForReview = sr.status === SR_STATUS.SUBMITTED;
  // Client approve/decline/request-info/hold belong to the client's Facilities Director ONLY.
  const showReview = canReview && user?.role === 'client_facilities_director';

  return (
    <div className="space-y-5" data-testid="service-request-detail">
      {/* Compact persistent Service Request banner (dark forest green) — sticky beneath the top bar */}
      <div className="relative sticky top-[63px] z-20 -mx-4 lg:-mx-8 -mt-6 border-b border-white/10 px-4 lg:px-8 pt-3 pb-3 lg:pb-9 text-white shadow-md" style={{ backgroundColor: DGREEN }} data-testid="sr-summary-banner">
        {/* MOBILE COMPACT summary (phones) */}
        <div className="lg:hidden" data-testid="sr-banner-compact">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#9ae6b4]">Service Request</span>
                <span className="font-mono text-xs font-bold text-[#9ae6b4]" data-testid="sr-detail-number-m">{sr.request_number}</span>
                {editing ? (
                  <select value={draft.status} onChange={(e) => dset('status', e.target.value)} data-testid="sr-edit-status-m"
                    className="max-w-[9rem] rounded-md border border-white/40 bg-white/10 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                    {Array.from(new Set([draft.status, ...Object.values(SR_STATUS)].filter(Boolean))).map((s) => <option key={s} value={s} className="text-black">{s}</option>)}
                  </select>
                ) : (<StatusBadge status={sr.status} />)}
              </div>
              <h1 className="mt-1 text-[15px] font-semibold leading-snug text-white line-clamp-2" data-testid="sr-detail-title-m">{srShortTitle(sr.title)}</h1>
              <div className="mt-1 flex items-center gap-1.5">
                <CustomerLogoImg customerId={sr.customer_id} token={token} />
                <span className="truncate text-[13px] font-medium text-emerald-50" data-testid="sr-customer-m">{sr.customer_name || '—'}{sr.location_name ? ` · ${sr.location_name}` : ''}</span>
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-center">
              <div className="flex h-9 w-9 items-center justify-center rounded-md bg-white text-[#14432A] shadow"><span className="text-base font-extrabold" data-testid="sr-priority-m">{sr.priority_level || '—'}</span></div>
              <span className="mt-0.5 text-[9px] uppercase tracking-wide text-[#9ae6b4]">Priority</span>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" className="h-7 border-white/40 bg-transparent px-2.5 text-xs text-white hover:bg-white/10" onClick={() => setHistOpen(true)} data-testid="sr-history-btn-m"><History size={12} className="mr-1" />History</Button>
            {canEdit && (editing ? (
              <>
                <Button size="sm" className="h-7 bg-[#8FC93A] px-3 text-xs font-semibold text-[#14432A] hover:bg-[#7fb82f]" disabled={savingEdit} onClick={saveEdit} data-testid="sr-banner-save-btn-m"><Save size={12} className="mr-1" />{savingEdit ? 'Saving…' : 'Save'}</Button>
                <Button size="sm" variant="outline" className="h-7 border-white/40 bg-transparent px-3 text-xs text-white hover:bg-white/10" disabled={savingEdit} onClick={cancelEdit} data-testid="sr-banner-cancel-btn-m"><X size={12} className="mr-1" />Cancel</Button>
              </>
            ) : (
              <Button size="sm" variant="outline" className="h-7 border-white/40 bg-transparent px-3 text-xs text-white hover:bg-white/10" onClick={enterEdit} data-testid="sr-banner-edit-btn-m"><Wrench size={12} className="mr-1" />Edit</Button>
            ))}
            {canPurge && !editing && <Button size="sm" variant="outline" className="h-7 border-red-300/60 bg-transparent px-2.5 text-xs text-red-200 hover:bg-red-500/20" onClick={openPurge} data-testid="sr-purge-btn-m"><Trash2 size={12} className="mr-1" />Purge</Button>}
          </div>
        </div>

        {/* DESKTOP FULL banner (lg+) */}
        <div className="hidden lg:block">
        <div className="flex flex-wrap lg:flex-nowrap items-start justify-between gap-x-8 gap-y-3">
          <div className="min-w-0 max-w-full lg:max-w-[44%]">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#9ae6b4]">Service Request</span>
              <span className="font-mono text-sm font-bold text-[#9ae6b4]" data-testid="sr-detail-number">{sr.request_number}</span>
              {editing ? (
                <select value={draft.status} onChange={(e) => dset('status', e.target.value)} data-testid="sr-edit-status"
                  className="max-w-[10rem] rounded-md border border-white/40 bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-white">
                  {Array.from(new Set([draft.status, ...Object.values(SR_STATUS)].filter(Boolean))).map((s) => <option key={s} value={s} className="text-black">{s}</option>)}
                </select>
              ) : (<StatusBadge status={sr.status} />)}
            </div>
            <div className="mt-1 flex items-center gap-2">
              <CustomerLogoImg customerId={sr.customer_id} token={token} />
              <span className="text-[15px] font-bold text-white" data-testid="sr-customer">{sr.customer_name || '—'}</span>
            </div>
            <div className="text-[13px] leading-tight text-emerald-100" data-testid="sr-location-name">{sr.location_name || '—'}{sr.area_name ? ` · ${sr.area_name}` : ''}</div>
            {(sr.location_address || sr.location_city) && <div className="text-[12px] leading-tight text-emerald-200" data-testid="sr-location-address">{[sr.location_address, [sr.location_city, sr.location_state].filter(Boolean).join(', '), sr.location_zip].filter(Boolean).join(', ')}</div>}
            {sr.location_code && <div className="text-[11px] text-emerald-300">Location #<span className="font-mono">{sr.location_code}</span></div>}
            {editing ? (
              <Input value={draft.title} onChange={(e) => dset('title', e.target.value)} data-testid="sr-edit-title"
                className="mt-1 h-7 border-white/40 bg-white/10 text-sm text-white" />
            ) : (<h1 className="mt-1 text-base font-semibold text-white" data-testid="sr-detail-title">{srShortTitle(sr.title)}</h1>)}
          </div>
          <div className="min-w-0 space-y-2">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-[#9ae6b4]">Requested By <span className="normal-case text-emerald-200">(client)</span></div>
              {editing ? (
                <Input value={draft.requester_contact_name} onChange={(e) => dset('requester_contact_name', e.target.value)} placeholder="Client requester" data-testid="sr-edit-requested-by"
                  className="mt-0.5 h-6 w-full max-w-[11rem] border-white/40 bg-white/10 px-2 py-0 text-sm text-white" />
              ) : (
                (sr.requester_contact_name || (!sr.entered_by_tfs && sr.requested_by_name))
                  ? <div className="text-sm font-medium text-white" data-testid="sr-requested-by">{sr.requester_contact_name || sr.requested_by_name}</div>
                  : <div className="flex items-center gap-1 text-sm font-semibold text-amber-300" data-testid="sr-requested-by-flag"><AlertTriangle size={12} /> Needs correction</div>
              )}
            </div>
            <SRBannerField label="Entered By">{sr.entered_by_name || (sr.entered_by_tfs ? sr.requested_by_name : '—')}</SRBannerField>
            {sr.client_authorized_by && <SRBannerField label="Authorized By">{sr.client_authorized_by || '—'}</SRBannerField>}
            <SRBannerField label="Service Class">{sr.service_scope || sr.service_classification || '—'}</SRBannerField>
          </div>
          <div className="flex shrink-0 items-start gap-3">
            <div>
              {editing ? (
                <>
                  <div><div className="text-[10px] uppercase tracking-wide text-[#9ae6b4]">Requested On</div>
                    <Input type="date" value={draft.original_request_date} onChange={(e) => dset('original_request_date', e.target.value)} data-testid="sr-edit-requested-on" className="mt-0.5 h-6 w-[8rem] border-white/40 bg-white/10 px-2 py-0 text-xs text-white" /></div>
                  <div className="mt-1.5"><div className="text-[10px] uppercase tracking-wide text-[#9ae6b4]">Required By</div>
                    <Input type="date" value={draft.client_required_completion_date} onChange={(e) => dset('client_required_completion_date', e.target.value)} data-testid="sr-edit-required-by" className="mt-0.5 h-6 w-[8rem] border-white/40 bg-white/10 px-2 py-0 text-xs text-white" /></div>
                </>
              ) : (
                <>
                  <SRBannerField label="Requested On">{fmtDate(sr.original_request_date || sr.submitted_at)}</SRBannerField>
                  <div className="mt-2"><SRBannerField label="Required By">{fmtDate(sr.client_required_completion_date)}</SRBannerField></div>
                </>
              )}
            </div>
            <div className="flex flex-col items-center">
              {editing ? (
                <select value={draft.priority_level} onChange={(e) => dset('priority_level', e.target.value)} data-testid="sr-edit-priority"
                  className="h-14 w-14 rounded-lg bg-white text-center text-lg font-extrabold text-[#14432A] shadow">
                  <option value="">—</option>
                  {PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.value}</option>)}
                </select>
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-white text-[#14432A] shadow" data-testid="sr-priority"><span className="text-2xl font-extrabold">{sr.priority_level || '—'}</span></div>
              )}
              <span className="mt-1 text-[10px] uppercase tracking-wide text-[#9ae6b4]">Priority</span>
            </div>
          </div>
        </div>
        {/* Edit / Save / Cancel + History + Purge — bottom-right, out of the summary flow */}
        <div className="absolute bottom-2 right-4 lg:right-8 flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-7 border-white/50 bg-transparent px-2.5 text-white hover:bg-white/10" onClick={() => setHistOpen(true)} data-testid="sr-history-btn"><History size={13} className="mr-1" />History</Button>
          {canPurge && !editing && <Button size="sm" variant="outline" className="h-7 border-red-300/60 bg-transparent px-2.5 text-red-200 hover:bg-red-500/20" onClick={openPurge} data-testid="sr-purge-btn"><Trash2 size={13} className="mr-1" />Purge</Button>}
          {canEdit && (editing ? (
            <>
              <Button size="sm" className="h-7 bg-[#8FC93A] px-3 font-semibold text-[#14432A] hover:bg-[#7fb82f]" disabled={savingEdit} onClick={saveEdit} data-testid="sr-banner-save-btn"><Save size={13} className="mr-1" />{savingEdit ? 'Saving…' : 'Save'}</Button>
              <Button size="sm" variant="outline" className="h-7 border-white/50 bg-transparent px-3 text-white hover:bg-white/10" disabled={savingEdit} onClick={cancelEdit} data-testid="sr-banner-cancel-btn"><X size={13} className="mr-1" />Cancel</Button>
            </>
          ) : (
            <Button size="sm" variant="outline" className="h-7 border-white/50 bg-transparent px-3 text-white hover:bg-white/10" onClick={enterEdit} data-testid="sr-banner-edit-btn"><Wrench size={13} className="mr-1" />Edit</Button>
          ))}
        </div>
        </div>
      </div>

      {/* Mobile-only expandable details — scrolls with the document */}
      <div className="lg:hidden rounded-xl border border-slate-200 bg-white" data-testid="sr-mobile-details">
        <button onClick={() => setMDetails((o) => !o)} className="flex w-full items-center justify-between px-4 py-2.5 text-sm font-semibold text-[#14432A]" data-testid="sr-mobile-details-toggle">
          <span>Details</span><ChevronDown size={16} className={`transition-transform ${mDetails ? 'rotate-180' : ''}`} />
        </button>
        {mDetails && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 border-t border-slate-100 px-4 py-3 text-sm" data-testid="sr-mobile-details-body">
            <div className="col-span-2"><div className="text-[10px] uppercase tracking-wide text-slate-400">Address</div><div className="text-slate-700">{[sr.location_address, [sr.location_city, sr.location_state].filter(Boolean).join(', '), sr.location_zip].filter(Boolean).join(', ') || '—'}{sr.location_code ? ` · #${sr.location_code}` : ''}</div></div>
            <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Requested By (client)</div><div className="text-slate-700">{sr.requester_contact_name || (!sr.entered_by_tfs && sr.requested_by_name) || '—'}</div></div>
            <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Entered By</div><div className="text-slate-700">{sr.entered_by_name || (sr.entered_by_tfs ? sr.requested_by_name : '—')}</div></div>
            <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Service Class</div><div className="text-slate-700">{sr.service_scope || sr.service_classification || '—'}</div></div>
            {sr.client_authorized_by && <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Authorized By</div><div className="text-slate-700">{sr.client_authorized_by}</div></div>}
            <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Requested On</div><div className="text-slate-700">{fmtDate(sr.original_request_date || sr.submitted_at)}</div></div>
            <div><div className="text-[10px] uppercase tracking-wide text-slate-400">Required By</div><div className="text-slate-700">{fmtDate(sr.client_required_completion_date)}</div></div>
          </div>
        )}
      </div>

      {/* Toolbar: Back + review/convert + acknowledgment */}
      <div className="flex flex-wrap items-center justify-between gap-2" data-testid="sr-toolbar">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#14432A]" data-testid="sr-detail-back"><ArrowLeft size={16} /> Back</button>
        <div className="flex flex-wrap items-center gap-2">
          {showReview && Object.entries(ACTIONS).map(([key, cfg]) => {
            const Icon = cfg.icon;
            return (<Button key={key} size="sm" className={`text-white ${cfg.tone}`} onClick={() => { setDialog(key); setNote(''); }} data-testid={`sr-action-${key}`}><Icon size={15} className="mr-1.5" /> {cfg.label}</Button>);
          })}
          {canConvert && sr.status === SR_STATUS.APPROVED && (
            <Button className="bg-[#8FC93A] hover:bg-[#7fb82f] text-[#14432A] font-semibold" disabled={busy} onClick={openConvert} data-testid="sr-create-wo-btn"><Wrench size={16} className="mr-1.5" /> Create Work Order</Button>
          )}
          {sr.work_order_id && onConverted && (
            <button onClick={() => onConverted?.(sr.work_order_id)} data-testid="sr-view-wo-link" className="text-sm font-semibold text-[#14432A] underline underline-offset-2">Open linked Work Order →</button>
          )}
          {sr.work_order_id && !onConverted && (<span className="text-xs font-semibold text-green-700" data-testid="sr-wo-created-badge">Work Order created ✓</span>)}
          {isOps && (<>
            <Button variant="outline" size="sm" onClick={openAck} disabled={ackBusy} data-testid="sr-ack-preview-btn"><Mail size={15} className="mr-1.5" />{ackBusy ? 'Loading…' : 'Acknowledgment'}</Button>
            <Button size="sm" className="bg-[#14432A] hover:bg-[#1c5c39]" onClick={downloadAckPdf} data-testid="sr-ack-pdf-btn"><Download size={15} className="mr-1.5" />PDF</Button>
          </>)}
        </div>
      </div>

      <div className="space-y-5">
        {/* Details */}
        <div className="space-y-5" data-testid="sr-section-details">
          <Card className="border-slate-200 shadow-sm rounded-2xl">
            <CardHeader className="border-b border-slate-100"><CardTitle className="text-sm font-bold uppercase tracking-wide text-slate-500">Request Details</CardTitle></CardHeader>
            <CardContent className="space-y-5 pt-5">
              {editing ? (
                <div className="space-y-3" data-testid="sr-edit-body">
                  <div className="lg:hidden grid grid-cols-1 gap-3 pb-3 border-b border-slate-100" data-testid="sr-edit-mobile-header-fields">
                    <div><Label className="text-xs">Title</Label><Input value={draft.title} onChange={(e) => dset('title', e.target.value)} data-testid="sr-edit-title-m" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">Priority</Label>
                        <select value={draft.priority_level} onChange={(e) => dset('priority_level', e.target.value)} data-testid="sr-edit-priority-m" className="w-full h-9 rounded-md border border-slate-200 px-2 text-sm">
                          <option value="">—</option>{PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.value}</option>)}
                        </select>
                      </div>
                      <div><Label className="text-xs">Requester (client)</Label><Input value={draft.requester_contact_name} onChange={(e) => dset('requester_contact_name', e.target.value)} data-testid="sr-edit-requested-by-m" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs">Requested On</Label><Input type="date" value={draft.original_request_date} onChange={(e) => dset('original_request_date', e.target.value)} data-testid="sr-edit-requested-on-m" /></div>
                      <div><Label className="text-xs">Required By</Label><Input type="date" value={draft.client_required_completion_date} onChange={(e) => dset('client_required_completion_date', e.target.value)} data-testid="sr-edit-required-by-m" /></div>
                    </div>
                  </div>
                  <div><Label className="text-xs">Description</Label><Textarea rows={4} value={draft.description} onChange={(e) => dset('description', e.target.value)} data-testid="sr-edit-description" /></div>
                  <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 pt-2 border-t border-slate-100">
                    <div><Label className="text-xs">Requester Email</Label><Input value={draft.requester_contact_email} onChange={(e) => dset('requester_contact_email', e.target.value)} data-testid="sr-edit-requester-email" /></div>
                    <div><Label className="text-xs">Requester Phone</Label><Input value={draft.requester_contact_phone} onChange={(e) => dset('requester_contact_phone', e.target.value)} data-testid="sr-edit-requester-phone" /></div>
                    <div><Label className="text-xs">Authorized By</Label><Input value={draft.client_authorized_by} onChange={(e) => dset('client_authorized_by', e.target.value)} data-testid="sr-edit-authorizer" /></div>
                    <div><Label className="text-xs">Authorizer Email</Label><Input value={draft.client_authorized_by_email} onChange={(e) => dset('client_authorized_by_email', e.target.value)} data-testid="sr-edit-authorizer-email" /></div>
                    <div><Label className="text-xs">Client Reference #</Label><Input value={draft.client_reference_number} onChange={(e) => dset('client_reference_number', e.target.value)} data-testid="sr-edit-client-ref" /></div>
                    <div><Label className="text-xs">Client PO #</Label><Input value={draft.client_po_number} onChange={(e) => dset('client_po_number', e.target.value)} data-testid="sr-edit-client-po" /></div>
                    <div><Label className="text-xs">Client WO #</Label><Input value={draft.client_work_order_number} onChange={(e) => dset('client_work_order_number', e.target.value)} data-testid="sr-edit-client-wo" /></div>
                    <div><Label className="text-xs">Service Classification</Label><Input value={draft.service_classification} onChange={(e) => dset('service_classification', e.target.value)} data-testid="sr-edit-classification" /></div>
                    <div><Label className="text-xs">Client Urgency</Label><Input value={draft.request_type} onChange={(e) => dset('request_type', e.target.value)} data-testid="sr-edit-urgency" /></div>
                  </div>
                  <p className="text-[11px] text-slate-400">Customer & location links and entry attribution are preserved; only business fields change. Entered by <b>{sr.entered_by_name || (sr.entered_by_tfs ? sr.requested_by_name : '—')}</b>.</p>
                </div>
              ) : (
                <p className="text-sm leading-relaxed text-slate-700 whitespace-pre-wrap" data-testid="sr-detail-description">{sr.description}</p>
              )}
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5 pt-4 border-t border-slate-100">
                <div className="flex items-start gap-2.5 text-sm"><Building2 size={16} className="text-[#8FC93A] mt-0.5" /><div><div className="text-slate-400 text-[11px] uppercase tracking-wide">Customer</div><div className="text-slate-700 font-medium">{sr.customer_name}</div></div></div>
                <div className="flex items-start gap-2.5 text-sm"><MapPin size={16} className="text-[#8FC93A] mt-0.5" /><div><div className="text-slate-400 text-[11px] uppercase tracking-wide">Location{sr.area_name ? ' / Area' : ''}</div>
                  <div className="text-slate-700 font-medium" data-testid="sr-location-name">{sr.location_name}{sr.area_name ? ` · ${sr.area_name}` : ''}</div>
                  {(sr.location_address || sr.location_city) && (
                    <div className="text-slate-500 mt-0.5" data-testid="sr-location-address">
                      {[sr.location_address, [sr.location_city, sr.location_state].filter(Boolean).join(', '), sr.location_zip].filter(Boolean).join(', ')}
                    </div>
                  )}
                  {sr.location_code && <div className="text-slate-400 text-xs mt-0.5">Location #<span className="font-mono font-semibold text-slate-600">{sr.location_code}</span></div>}
                </div></div>
                <div className="flex items-start gap-2.5 text-sm"><User size={16} className="text-[#8FC93A] mt-0.5" /><div><div className="text-slate-400 text-[11px] uppercase tracking-wide">Requested by</div><div className="text-slate-700 font-medium" data-testid="sr-requestor">{formatRequestor(sr.requested_by_name, sr.customer_name, sr.entered_by_tfs)}</div></div></div>
                <div className="flex items-start gap-2.5 text-sm"><Clock size={16} className="text-[#8FC93A] mt-0.5" /><div><div className="text-slate-400 text-[11px] uppercase tracking-wide">Submitted</div><div className="text-slate-700 font-medium" data-testid="sr-submitted">{fmtDateTime(sr.submitted_at)}</div></div></div>
              </div>

              {/* Reference & Intake */}
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5 pt-4 border-t border-slate-100" data-testid="sr-intake-block">
                {sr.execution_authorization && (
                  <div className="flex items-start gap-2.5 text-sm sm:col-span-2"><ShieldCheck size={16} className={sr.execution_authorization === 'Pre-Approved to Proceed' ? 'text-[#8FC93A] mt-0.5' : 'text-amber-500 mt-0.5'} /><div><div className="text-slate-400 text-[11px] uppercase tracking-wide">Execution Authorization</div><div className="font-semibold" data-testid="sr-execution-auth">{sr.execution_authorization}{sr.authorization_method ? ` · ${sr.authorization_method}` : ''}{sr.authorized_nte_amount ? ` · NTE $${sr.authorized_nte_amount}` : ''}</div>{sr.authorization_evidence_ref && <div className="text-[11px] text-slate-400">Evidence: {sr.authorization_evidence_ref}</div>}</div></div>
                )}
                <div className="flex items-start gap-2.5 text-sm"><AlertTriangle size={16} className="text-[#8FC93A] mt-0.5" /><div><div className="text-slate-400 text-[11px] uppercase tracking-wide">Client-Reported Urgency</div><div className="text-slate-700 font-medium" data-testid="sr-client-urgency">{sr.request_type || 'Standard'}</div></div></div>
                {sr.priority_level ? (
                  <div className="flex items-start gap-2.5 text-sm"><CalendarClock size={16} className="text-[#8FC93A] mt-0.5" /><div><div className="text-slate-400 text-[11px] uppercase tracking-wide">Operational Priority (TFS)</div><div className="text-slate-700 font-medium" data-testid="sr-priority">{PRIORITY_LABELS[sr.priority_level] || sr.priority_level}</div></div></div>
                ) : sr.priority_needs_review ? (
                  <div className="flex items-start gap-2.5 text-sm"><CalendarClock size={16} className="text-amber-500 mt-0.5" /><div><div className="text-slate-400 text-[11px] uppercase tracking-wide">Operational Priority (TFS)</div><div className="text-amber-700 font-medium" data-testid="sr-priority">Pending authorized review</div></div></div>
                ) : null}
                {(sr.service_scope || sr.service_classification) && (
                  <div className="flex items-start gap-2.5 text-sm"><ShieldCheck size={16} className={(sr.service_scope || sr.service_classification) === 'Above Contract Scope' ? 'text-yellow-600 mt-0.5' : 'text-slate-500 mt-0.5'} /><div><div className="text-slate-400 text-[11px] uppercase tracking-wide">Service Classification</div><div className="text-slate-700 font-medium" data-testid="sr-service-scope">{sr.service_scope || sr.service_classification}</div></div></div>
                )}
                <div className="flex items-start gap-2.5 text-sm"><CalendarClock size={16} className="text-[#8FC93A] mt-0.5" /><div><div className="text-slate-400 text-[11px] uppercase tracking-wide">Client-Required Completion</div><div className="text-slate-700 font-medium" data-testid="sr-required-date">{fmtDate(sr.client_required_completion_date)}</div></div></div>
                <div className="flex items-start gap-2.5 text-sm"><Mail size={16} className="text-[#8FC93A] mt-0.5" /><div><div className="text-slate-400 text-[11px] uppercase tracking-wide">Intake Source</div><div className="text-slate-700 font-medium" data-testid="sr-intake-source">{sr.intake_source || '—'}{sr.source_system ? ` · ${sr.source_system}` : ''}</div></div></div>
                <div className="flex items-start gap-2.5 text-sm"><CalendarClock size={16} className="text-[#8FC93A] mt-0.5" /><div><div className="text-slate-400 text-[11px] uppercase tracking-wide">Original / Received by TFS</div><div className="text-slate-700 font-medium" data-testid="sr-intake-dates">{fmtDate(sr.original_request_date)} · {fmtDate(sr.received_by_tfs_date)}</div></div></div>
                {(sr.client_location_code || sr.client_site_identifier) && (
                  <div className="flex items-start gap-2.5 text-sm"><Hash size={16} className="text-[#8FC93A] mt-0.5" /><div><div className="text-slate-400 text-[11px] uppercase tracking-wide">Client Identifiers</div><div className="text-slate-700 font-medium font-mono" data-testid="sr-client-ids">{[sr.client_location_code, sr.client_site_identifier].filter(Boolean).join(' · ')}</div></div></div>
                )}
                {(sr.client_reference_number || sr.client_po_number || sr.client_work_order_number) && (
                  <div className="flex items-start gap-2.5 text-sm"><Hash size={16} className="text-[#8FC93A] mt-0.5" /><div><div className="text-slate-400 text-[11px] uppercase tracking-wide">Client Ref / PO / WO</div><div className="text-slate-700 font-medium" data-testid="sr-client-refs">{[sr.client_reference_number, sr.client_po_number, sr.client_work_order_number].filter(Boolean).join(' · ') || '—'}</div></div></div>
                )}
              </div>

              {/* Request Chain — three distinct roles (never combined) */}
              {(sr.requester_contact_name || sr.client_authorized_by || sr.entered_by_tfs) && (
                <RequestChainCard
                  record={sr}
                  variant="panel"
                  prefix="sr"
                  showAuthorizedBadge
                  showReceived
                  enteredByFallback={sr.requested_by_name}
                />
              )}
              {sr.review_notes && (
                <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 text-sm text-blue-800">
                  <div className="flex items-center gap-1.5 font-semibold text-xs mb-1"><MessageSquare size={13} /> Reviewer note</div>
                  {sr.review_notes}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Attachments (photos + supporting documents) */}
          <Card className="border-slate-200 shadow-sm rounded-2xl mt-6" data-testid="sr-attachments-card">
            <CardHeader className="border-b border-slate-100"><CardTitle className="text-sm font-bold uppercase tracking-wide text-slate-500 flex items-center gap-2"><Paperclip size={15} /> Attachments</CardTitle></CardHeader>
            <CardContent className="space-y-4 pt-5">
              {['before', 'after', 'supporting'].map((cat) => (
                <div key={cat}>
                  <div className="flex items-center justify-between">
                    <div className="text-slate-500 text-xs font-semibold uppercase tracking-wide">{cat === 'before' ? 'Before Photos' : cat === 'after' ? 'After Photos' : 'Supporting Photos'}</div>
                    {canAttach && (
                      <label className="inline-flex items-center gap-1 text-xs font-semibold text-[#0E2A47] hover:underline cursor-pointer" data-testid={`sr-att-upload-${cat}`}>
                        <Upload size={13} /> {attUploading === cat ? 'Uploading…' : 'Add'}
                        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { uploadAttachment(e.target.files?.[0], cat); e.target.value = ''; }} />
                      </label>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    {attByCat(cat).map((a) => (
                      <div key={a.id} className="relative group">
                        <AuthImage src={`${API}/service-requests/attachments/${a.id}`} token={token} alt={a.caption || cat} caption={a.caption || ''} className="h-20 w-20 rounded border" testId={`sr-att-${cat}`} />
                        {canRemove && <button onClick={() => removeAttachment(a.id)} className="absolute -top-1.5 -right-1.5 bg-white rounded-full border shadow p-0.5 opacity-0 group-hover:opacity-100 z-10" data-testid="sr-att-remove"><Trash2 size={12} className="text-red-500" /></button>}
                      </div>
                    ))}
                    {attByCat(cat).length === 0 && <span className="text-xs text-slate-300">None</span>}
                  </div>
                </div>
              ))}
              <div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-slate-500 text-xs font-semibold uppercase tracking-wide">Documents</div>
                  {canAttach && (
                    <div className="flex items-center gap-2 min-w-0">
                      <select value={docCat} onChange={(e) => setDocCat(e.target.value)} className="text-xs border border-slate-200 rounded px-1.5 py-1 w-[150px] max-w-full" data-testid="sr-att-doc-category">
                        {['Receipt', 'Purchase Document', 'Permit', 'Sign-Off', 'Vendor Document', 'Other Supporting Document'].map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <label className="inline-flex items-center gap-1 text-xs font-semibold text-[#0E2A47] hover:underline cursor-pointer" data-testid="sr-att-upload-document">
                        <Upload size={13} /> {attUploading === 'document' ? 'Uploading…' : 'Add'}
                        <input type="file" className="hidden" onChange={(e) => { uploadAttachment(e.target.files?.[0], 'document'); e.target.value = ''; }} />
                      </label>
                    </div>
                  )}
                </div>
                <div className="mt-1.5 space-y-1">
                  {attByCat('document').map((a) => (
                    <div key={a.id} className="flex items-center gap-2 text-sm" data-testid="sr-att-document">
                      <a href={`${API}/service-requests/attachments/${a.id}`} target="_blank" rel="noreferrer" className="text-[#0E2A47] hover:underline flex items-center gap-1.5"><FileText size={14} /> {a.filename}{a.doc_category ? ` · ${a.doc_category}` : ''}</a>
                      {canRemove && <button onClick={() => removeAttachment(a.id)} data-testid="sr-att-doc-remove"><Trash2 size={13} className="text-red-400 hover:text-red-600" /></button>}
                    </div>
                  ))}
                  {attByCat('document').length === 0 && <span className="text-xs text-slate-300">None</span>}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Conversion dialog */}
      <Dialog open={convOpen} onOpenChange={(o) => !o && setConvOpen(false)}>
        <DialogContent data-testid="sr-convert-dialog">
          <DialogHeader><DialogTitle>Create Work Order</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              Client urgency: <span className="font-semibold">{sr.request_type}</span>. Assign the official TFS priority below (kept separate).
            </p>
            <div>
              <Label>TFS Priority (P1–P5) *</Label>
              <Select value={conv.priority_level} onValueChange={(v) => setConv((c) => ({ ...c, priority_level: v }))}>
                <SelectTrigger data-testid="conv-priority-select"><SelectValue placeholder="Select priority" /></SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Required Completion Date</Label>
              <Input type="date" data-testid="conv-date-input" value={conv.required_completion_date}
                onChange={(e) => setConv((c) => ({ ...c, required_completion_date: e.target.value }))} />
            </div>
            <div>
              <Label>Fulfillment Type</Label>
              <Select value={conv.fulfillment_type} onValueChange={(v) => setConv((c) => ({ ...c, fulfillment_type: v, assigned_to: '' }))}>
                <SelectTrigger data-testid="conv-fulfillment-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FULFILLMENT_OPTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {conv.fulfillment_type === 'TFS Internal Personnel' && (
              <div>
                <Label>Assign To (TFS personnel)</Label>
                <Select value={conv.assigned_to} onValueChange={(v) => setConv((c) => ({ ...c, assigned_to: v }))}>
                  <SelectTrigger data-testid="conv-assignee-select"><SelectValue placeholder="Select personnel (optional)" /></SelectTrigger>
                  <SelectContent>
                    {fieldUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvOpen(false)}>Cancel</Button>
            <Button className="bg-[#0E2A47] hover:bg-[#16324F]" disabled={busy}
              onClick={runConvert} data-testid="conv-confirm-btn">
              {busy ? 'Creating…' : 'Create Work Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Acknowledgment email preview (preview only — nothing is sent) */}
      <Dialog open={ackOpen} onOpenChange={(o) => !o && setAckOpen(false)}>
        <DialogContent className="max-w-2xl" data-testid="sr-ack-dialog">
          <DialogHeader><DialogTitle>Acknowledgment Email — Preview</DialogTitle></DialogHeader>
          {ackData && (
            <div className="space-y-3">
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="sr-ack-notsent">
                {ackData.not_sent_notice}
              </div>
              <div className="grid grid-cols-[90px_1fr] gap-y-2 text-sm">
                <div className="text-slate-400">To</div><div className="font-medium text-[#0E2A47]" data-testid="sr-ack-recipient">{ackData.recipient}</div>
                <div className="text-slate-400">Subject</div><div className="font-medium text-[#0E2A47]" data-testid="sr-ack-subject">{ackData.subject}</div>
                <div className="text-slate-400">Attachment</div>
                <div className="font-medium text-[#0E2A47] flex items-center gap-1.5" data-testid="sr-ack-attachment"><FileText size={14} className="text-[#8FC93A]" /> {ackData.attachment}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 whitespace-pre-wrap max-h-72 overflow-auto" data-testid="sr-ack-body">
                {ackData.body}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAckOpen(false)}>Close</Button>
            <Button className="bg-[#0E2A47] hover:bg-[#16324F]" onClick={downloadAckPdf} data-testid="sr-ack-dialog-pdf-btn">
              <Download size={15} className="mr-1.5" /> Open PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Action dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="sr-action-dialog">
          <DialogHeader><DialogTitle>{dialog && ACTIONS[dialog].title}</DialogTitle></DialogHeader>
          <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={dialog && ACTIONS[dialog].needsNote ? 'Required note…' : 'Optional note…'}
            data-testid="sr-action-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button className={`text-white ${dialog && ACTIONS[dialog].tone}`} disabled={busy}
              onClick={runAction} data-testid="sr-action-confirm">
              {busy ? 'Working…' : (dialog && ACTIONS[dialog].label)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* History (on-demand) */}
      <Dialog open={histOpen} onOpenChange={(o) => !o && setHistOpen(false)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="sr-history-dialog">
          <DialogHeader><DialogTitle>Activity History — {sr.request_number}</DialogTitle></DialogHeader>
          <ol className="relative border-l border-gray-200 ml-2 space-y-5 mt-2" data-testid="sr-activity-timeline">
            {activity.length === 0 && <li className="ml-4 text-sm text-slate-400">No activity recorded yet.</li>}
            {activity.map((a) => (
              <li key={a.id} className="ml-4">
                <span className="absolute -left-1.5 h-3 w-3 rounded-full bg-[#14432A] border-2 border-white" />
                <div className="text-sm font-medium text-slate-800">{ACTION_LABELS[a.action] || a.action}</div>
                <div className="text-xs text-slate-400">{a.actor_name} · {roleLabel(a.actor_role)}</div>
                <div className="text-xs text-slate-400">{fmtDateTime(a.timestamp)}</div>
                {a.message && <div className="text-xs text-slate-500 mt-1 italic">“{a.message}”</div>}
              </li>
            ))}
          </ol>
        </DialogContent>
      </Dialog>

      {/* Purge (Global Administrator only) */}
      <Dialog open={purgeOpen} onOpenChange={(o) => !o && setPurgeOpen(false)}>
        <DialogContent className="max-w-md" data-testid="sr-purge-dialog">
          <DialogHeader><DialogTitle className="text-red-700 flex items-center gap-2"><Trash2 size={18} /> Purge Service Request</DialogTitle></DialogHeader>
          {!purgeInfo ? <div className="py-6 text-center text-sm text-slate-400">Loading…</div> : (
            <div className="space-y-3">
              <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                This permanently removes the following and cannot be undone. Master data (customers, locations, personnel) is preserved.
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid="sr-purge-summary">
                <div><b>Request:</b> {purgeInfo.request_number} — {purgeInfo.title}</div>
                <div><b>Status:</b> {purgeInfo.status}</div>
                {purgeInfo.linked_work_order && <div className="text-red-700"><b>Linked Work Order:</b> {purgeInfo.linked_work_order} (will also be purged)</div>}
                <div><b>SR attachments:</b> {purgeInfo.sr_attachments} · <b>WO media:</b> {(purgeInfo.wo_media || 0) + (purgeInfo.wo_photos || 0)}</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeOpen(false)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={busy || !purgeInfo} onClick={runPurge} data-testid="sr-purge-confirm">{busy ? 'Purging…' : 'Purge permanently'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServiceRequestDetail;
